import { FC, MutableRefObject, useEffect, useRef } from "react";

import {
  IMapLibrePolygonEditorHandle,
  MapLibrePolygonEditor,
} from "@ryde/controls";
import { IGeoJSON } from "@ryde/shared";

import {
  AREA_POLYGON_EDITING_FILL_OPACITY,
  AREA_POLYGON_EDITING_WEIGHT,
} from "./area-polygons-map.constants";
import { IAreaPolygonEditHandle } from "./area-polygons-map.models";

interface IAreaPolygonEditLayerProps {
  // The area being edited owns this layer; its color keeps the polygon
  // recognisable while it is highlighted for editing.
  color: string;
  value?: IGeoJSON;
  handleRef: MutableRefObject<IAreaPolygonEditHandle | undefined>;
}

/**
 * One area's polygons, made reshapeable: drag a corner, drag a midpoint to add one, right-click one to
 * remove it, or long-press the body to move the whole shape. Mounted only while that area is being edited,
 * and the page reads the result back through `handleRef` when the user saves.
 *
 * A thin wrapper: the gestures all live in the shared editor, which the polygon drawer uses too. What is
 * specific here is that only reshaping is offered — no drawing, cutting or deleting, which are the drawer's
 * tools — and that the shape keeps its area's colour rather than the drawer's red.
 */
export const AreaPolygonEditLayer: FC<IAreaPolygonEditLayerProps> = ({
  color,
  value,
  handleRef,
}) => {
  const editorRef = useRef<IMapLibrePolygonEditorHandle>();

  // The page's handle names the read `getEditedGeoJson`; the shared editor calls it `getGeoJson`. Adapting
  // here keeps the areas page unaware of which editor is underneath.
  useEffect(() => {
    handleRef.current = {
      getEditedGeoJson: (): IGeoJSON =>
        editorRef.current?.getGeoJson() ?? {
          type: "FeatureCollection",
          features: [],
        },
    };

    return () => {
      handleRef.current = undefined;
    };
  }, [handleRef]);

  return (
    <MapLibrePolygonEditor
      value={value}
      handleRef={editorRef}
      mode="edit"
      color={color}
      fillOpacity={AREA_POLYGON_EDITING_FILL_OPACITY}
      weight={AREA_POLYGON_EDITING_WEIGHT}
      // No fitPadding: the page has already framed these shapes and must not jump when one is picked up.
    />
  );
};
